import { useEffect, useRef, useCallback, useState } from 'react';

const WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:9001/ws`;
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;
const PING_INTERVAL_MS = 25000;

/**
 * useWebSocket — React hook for real-time WebSocket connection.
 *
 * Usage:
 *   const { isConnected, lastEvent } = useWebSocket(accessToken, {
 *     onNotification: (data) => { ... },
 *     onBookingUpdate: (data) => { ... },
 *     onPaymentUpdate: (data) => { ... },
 *   });
 *
 * Events pushed by server:
 *   - "notification" — new in-app notification
 *   - "booking_update" — booking status changed
 *   - "payment_update" — payment status changed
 *   - "connected" — auth confirmed
 */
export function useWebSocket(accessToken, handlers = {}) {
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const handlersRef = useRef(handlers);

  // Keep handlers ref up to date without re-triggering effect
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const cleanup = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!accessToken) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send auth token as first message
        ws.send(JSON.stringify({ token: accessToken }));
        reconnectAttempts.current = 0;

        // Start ping interval to keep connection alive
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        const text = event.data;

        // Ignore pong responses
        if (text === 'pong') return;

        try {
          const msg = JSON.parse(text);
          const { event: eventType, data } = msg;

          // Connection confirmed
          if (eventType === 'connected') {
            setIsConnected(true);
            return;
          }

          setLastEvent(msg);

          // Route to appropriate handler
          switch (eventType) {
            case 'notification':
              handlersRef.current.onNotification?.(data);
              break;
            case 'booking_update':
              handlersRef.current.onBookingUpdate?.(data);
              break;
            case 'payment_update':
              handlersRef.current.onPaymentUpdate?.(data);
              break;
            default:
              handlersRef.current.onMessage?.(msg);
          }
        } catch (e) {
          // Non-JSON message, ignore
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        if (pingTimer.current) {
          clearInterval(pingTimer.current);
          pingTimer.current = null;
        }

        // Don't reconnect if closed intentionally (code 4001-4003 = auth failure)
        if (event.code >= 4001 && event.code <= 4003) return;

        // Auto-reconnect with backoff
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAY_MS * Math.pow(1.5, reconnectAttempts.current);
          reconnectAttempts.current += 1;
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // onclose will fire after this
      };
    } catch (e) {
      // WebSocket constructor failed
    }
  }, [accessToken]);

  useEffect(() => {
    connect();
    return cleanup;
  }, [connect, cleanup]);

  return { isConnected, lastEvent };
}

export default useWebSocket;
